import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Tag, message } from "antd";
import { useLocation } from "react-router-dom";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { saveNow } from "components/SaveButton";
import { useAuth, useStreamEditorFeature } from "../../../../../providers/AuthProvider";
import { parseLinkTarget } from "../SubtitlesBlock/linkTarget";
import { langOf, openOwnFixIds, OwnFixPhase, ownFixStatus, StreamProgressData } from "../StreamProgress/model";
import { releaseFolder } from "../StreamProgress/release";

export const SITE_URL = "https://torsunov.com";

/** Язык сайта для плеера: редактор зовёт китайский «ch», сайт — «cn». */
export const siteLangOf = (lang: string | null | undefined): string => {
    const value = String(lang || "").toLowerCase();
    if (value === "ch" || value === "zh" || value === "cn") return "cn";
    return ["en", "de", "es", "ar", "ru"].includes(value) ? value : "en";
};

export const playerUrl = (mediaId: string, lang: string | null | undefined): string =>
    `${SITE_URL}/${siteLangOf(lang)}/player/${encodeURIComponent(mediaId)}`;

/** «← К эфиру»: вкладку открыл плеер — закрыть её (плеер стоит на паузе на этой реплике), иначе — в плеер. */
export const backToAir = (mediaId: string, lang: string | null | undefined, win: Window = window): void => {
    const url = playerUrl(mediaId, lang);
    try {
        win.close();
    } catch {
        /* closing refused — go to the player instead */
    }
    win.setTimeout(() => {
        if (!win.closed) win.location.href = url;
    }, 300);
};

/**
 * Какие языки отпускать: у перевода — его язык; у оригинала (правят русский эфира) — все языки эфира, правка
 * русского уходит в каждый.
 */
export const releaseLangs = (lang: string, originalLang: string | undefined, streamLangs: string[] | undefined): string[] => {
    if (lang && lang !== originalLang) return [lang];
    return (streamLangs || []).filter((one) => one && one !== originalLang);
};

export const SAVE_FAILED_TEXT = "Не сохранилось — попробуйте ещё раз";
export const DONE_TITLE = "Голос, видео папки и выкладка — дальше сами";

const PHASE_COLOR: Record<OwnFixPhase, string> = {
    none: "default",
    accepted: "processing",
    air: "success",
    record: "success",
    nothing: "default",
    failed: "error",
    ask: "warning",
    // Ответ на вопрос принят, а вопрос открыт (TAG: новая правка ответа ещё идёт или короткой правки ещё нет).
    answered: "processing",
    // Вопрос правки закрыт («оставлено как в эфире», новая правка той же реплики, проба снята) — не ждёт ответа.
    closed: "default",
};

interface Props {
    mediaId: string;
    folder: number;
    lang: string;
    isRealtimeStream: boolean;
    /** Опрос прогресса StreamProgress (нет опроса — null): по нему виден ход своей правки. */
    data: StreamProgressData | null;
    /** Отпустили папку — пусть опрос спросит сразу, не ждя 5 с. */
    onReleased?: () => void;
}

/**
 * «Готово — в эфир» у переводчика эфира (`features: stream_fixes`): сохранить сейчас → отпустить папку
 * (ждущие правки — в эфир сразу) → ход своей правки: «принята» → «в эфире · зрители увидят ЧЧ:ММ».
 * Голос, видео папки и выкладка дальше идут сами. Открыто из плеера (`from=player`) — «← К эфиру».
 * Обычной лекции (не эфир) нет ничего.
 */
export const StreamDone: React.FC<Props> = ({ mediaId, folder, lang, isRealtimeStream, data, onReleased }) => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const { user } = useAuth();
    const enabled = useStreamEditorFeature("stream_fixes");
    const location = useLocation();
    const { fromPlayer } = parseLinkTarget(location.search);
    const latest = useRef(state);
    latest.current = state;
    const [busy, setBusy] = useState<null | "save" | "release">(null);
    const [sent, setSent] = useState<string[] | null>(null);

    useEffect(() => setSent(null), [mediaId, folder, lang]);

    const langs = releaseLangs(lang, state.originalLang, state.realtimeEditorStatus?.target_languages);
    const langsKey = langs.join(",");
    const fixes = useMemo(() => {
        const current = data?.folders?.find((item) => item.folder === Number(folder));
        return langsKey.split(",").flatMap((one) => langOf(current, one)?.fixes || []);
    }, [data, folder, langsKey]);
    const status = sent ? ownFixStatus(fixes, sent) : null;

    if (!isRealtimeStream || (!enabled && !fromPlayer)) return null;
    const canSend = enabled && langs.length > 0;

    const onDone = async () => {
        if (busy) return;
        setBusy("save");
        try {
            await saveNow({
                flush: () => (handles.flushGate ? handles.flushGate() : handles.flushDirtyChanges()),
                read: () => ({
                    savingStatus: Boolean(latest.current.savingStatus),
                    autoSaveStatus: latest.current.autoSaveStatus,
                }),
            });
        } catch {
            message.error(SAVE_FAILED_TEXT);
            setBusy(null);
            return;
        }
        setBusy("release");
        const openBefore = openOwnFixIds(fixes, user?.email || null);
        try {
            const results = await Promise.all(langs.map((one) => releaseFolder(mediaId, Number(folder), one)));
            setSent(Array.from(new Set([...results.flatMap((one) => one.released), ...openBefore])));
            onReleased?.();
        } catch (err) {
            message.error(`Не ушло в эфир — попробуйте ещё раз (${(err as Error).message})`);
        } finally {
            setBusy(null);
        }
    };

    return (
        <span data-testid="stream-done" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 8 }}>
            {fromPlayer && (
                <a
                    data-testid="stream-back"
                    href={playerUrl(mediaId, lang)}
                    onClick={(event) => {
                        // Обычный клик — закрыть вкладку плеера; ссылка с колёсика/Ctrl открывает плеер.
                        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;
                        event.preventDefault();
                        backToAir(mediaId, lang);
                    }}
                    title="Вернуться в плеер эфира"
                    style={{ fontSize: 12, whiteSpace: "nowrap" }}
                >
                    ← К эфиру
                </a>
            )}
            {canSend && (
                <Button
                    type="primary"
                    size="small"
                    loading={Boolean(busy)}
                    onClick={() => void onDone()}
                    title={DONE_TITLE}
                    data-testid="stream-done-button"
                >
                    {busy === "save" ? "Сохраняю…" : busy === "release" ? "Отправляю…" : "Готово — в эфир"}
                </Button>
            )}
            {canSend && status && (
                <Tag
                    data-testid="stream-done-status"
                    color={PHASE_COLOR[status.phase]}
                    title={status.title}
                    style={{ marginInlineEnd: 0, fontSize: 11 }}
                >
                    {status.text}
                </Tag>
            )}
        </span>
    );
};
